import type { MaybeRefOrGetter, VNode } from "vue";
import { h, ref, toValue, watch } from "vue";

/**
 * Composable for handling close animations in dropdowns/menus.
 *
 * This handles the common pattern where we need to maintain an "animating" state
 * briefly after a menu closes to preserve styling during the transition.
 *
 * @param isOpen - Reactive reference to track whether the menu/dropdown is open
 * @param delay - Animation delay in milliseconds (default: 100ms)
 * @returns Object with isAnimating ref and AnimationPlaceholder component
 */
export function useCloseAnimation(isOpen: MaybeRefOrGetter<boolean>, delay = 100) {
  const isAnimating = ref(false);

  watch(() => toValue(isOpen), (value: boolean) => {
    if (!value) {
      isAnimating.value = true;
      setTimeout(() => {
        isAnimating.value = false;
      }, delay);
    }
  });

  /**
   * Render function for the animation placeholder div.
   * Use this in your template to automatically handle the animation state.
   */
  const AnimationPlaceholder = (): VNode | null => {
    if (!isAnimating.value) return null;

    return h("div", {
      "data-state": "open",
      class: "absolute hidden",
    });
  };

  return {
    isAnimating,
    AnimationPlaceholder,
  };
}
